import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { ListRowType, ListProps } from 'types/ListTypes';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { handleDragRow } from './functions/handleDragRow';
import { saveBatchData } from 'services/api/saveBatchData';
import { isRowSelected as checkRowSelected } from './functions/selectionUtils';
import ListContext from 'components/lists/ListContext';
import ListButtons from './ListButtons';
import ListRow from './ListRow';
import ListSelectionCheckbox from './ListSelectionCheckbox';
import BulkActionsPanel from 'components/bulkActions/BulkActionsPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faMinus } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/list.scss';
import '../../style/scss/tooltip.scss';
import '../../style/scss/bulkActions.scss';
import { groupDataByField } from './functions/groupDataByField';
import { saveData } from 'services/api/saveData';

const ListComponent = <T extends ListRowType>({
    query, objectName,
    filters,
    filterFields,
    quickFilterSize,
    activeFilters,
    setActiveFilters,
    filterDropdownData,
    showSearch, showDeleteOrDeactivate, detailPageUrl, isPaginated, pageSize = 50, isDraggable = false, backButton, headerTitle, actionButton,
    onRowClick, detailModal, releaseViewModal, wizardModal, postNewItem, openPageUrl, formModal, editModal, modalSize, showAddButton, tableClassName,
    showImport, showExport, showPerWeek, weekNavigation, showColumnSelector, detailModalSize, changeViewButton, buttonSize, detailModalName, groupByField,
    activeQuickFilter, refetchList, draggingApiUrl, renderHeaderInfo, renderTrailingInfo, renderNotice, getFormModal, getModalSize, getFormModalTitle,
    onActiveQuickFilterChange, secondaryButton, allowEditingInactiveRows, bulkActions, moreMenu,
    inactiveItemsActive, onToggleInactiveItems, onExportAll, panelInitialKind
}: ListProps<T>) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { queryParams, data, listMetaData, ordering, clearedOrderingColumn, loading, selectedColumns, displayColumns, tableMinWidth, listTooltip, setListTooltip, tableWidthClass,
        handlePageChange, handleColumnOrdering, handleDragOrdering, selection, selectedCount, toggleRowSelection, toggleSelectAll,
        hasCustomListColumns, handleColumnResizeEnd, handleColumnReorderEnd } = useContext(ListContext);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number, left: number } | null>(null);

    // Keep track of the moving ghost of the dragged column header
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [dragHighlight, setDragHighlight] = useState<{ left: number, top: number, width: number, height: number } | null>(null);

    // Holds a static clone of the dragged column shown inside the ghost
    const [dragGhostHtml, setDragGhostHtml] = useState<string | null>(null);

    // Determine wheter the list currently shows the deleted or deactivated items
    const inactiveItemsView = queryParams['deleted'] === 'true' || queryParams['deleted'] === true
        || queryParams['is_active'] === 'false' || queryParams['is_active'] === false;

    // Show a trailing filler column absorbing the leftover space in the full width layout
    const showFillerColumn = tableWidthClass === 'container-wide-filled';

    // Suppresses the sorting click which follows a completed header drag
    const suppressHeaderClickRef = useRef(false);

    // Keep track of the live column widths while dragging a resize handle
    const [resizeWidths, setResizeWidths] = useState<Record<string, string>>({});

    // Clear the live resize widths when the selected columns change
    useEffect(() => {
        setResizeWidths({});
    }, [selectedColumns]);

    // Start resizing a column from its header resize handle
    const startColumnResize = (event: React.MouseEvent, field: string) => {
        event.preventDefault();
        event.stopPropagation();

        // Measure the rendered pixel width of the header cell as the start width
        const headerCell = (event.currentTarget as HTMLElement).closest('th');
        const startWidth = headerCell ? headerCell.getBoundingClientRect().width : 100;
        const startX = event.clientX;

        // Update the live width of the column while the mouse moves
        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(40, startWidth + moveEvent.clientX - startX);
            setResizeWidths(previous => ({ ...previous, [field]: `${Math.round(newWidth)}px` }));
        };

        // Persist the final width once the mouse button is released
        const handleMouseUp = (upEvent: MouseEvent) => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            const finalWidth = Math.max(40, startWidth + upEvent.clientX - startX);
            handleColumnResizeEnd?.(field, Math.round(finalWidth));
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Start dragging a column header to reorder the columns
    const startColumnDrag = (event: React.MouseEvent, field: string) => {
        if (!hasCustomListColumns || !handleColumnReorderEnd || event.button !== 0) {
            return;
        }

        // Prevent the browser from selecting the header texts while dragging
        event.preventDefault();

        // Collect the real column header cells with their positions
        const headerRow = (event.currentTarget as HTMLElement).closest('tr');
        const table = (event.currentTarget as HTMLElement).closest('table');
        const wrapperRect = tableWrapperRef.current?.getBoundingClientRect();
        const sourceCell = (event.currentTarget as HTMLElement).closest('th');
        if (!headerRow || !table || !wrapperRect || !sourceCell) {
            return;
        }
        const cellRects = (Array.from(headerRow.querySelectorAll('th.resizable-column')) as HTMLElement[])
            .map(cell => cell.getBoundingClientRect());
        const tableRect = table.getBoundingClientRect();
        const startX = event.clientX;

        // Measure the dragged column itself, the ghost keeps this exact width
        const sourceRect = sourceCell.getBoundingClientRect();
        const grabOffset = startX - sourceRect.left;

        let dragging = false;
        let targetIndex: number | null = null;

        // Build a static clone of the whole dragged column for the ghost
        const buildGhostHtml = () => {
            const headerCells = Array.from(headerRow.querySelectorAll('th.resizable-column')) as HTMLElement[];
            const sourceIndex = headerCells.indexOf(sourceCell);
            if (sourceIndex === -1) {
                return null;
            }

            // The body rows hold leading cells for selection and dragging
            const leadingCellCount = (bulkActions ? 1 : 0) + (isDraggable ? 1 : 0);

            // Rebuild the column as a single column list table
            const ghostTable = document.createElement('table');
            ghostTable.className = table.className;
            const ghostHead = document.createElement('thead');
            const ghostHeaderRow = headerRow.cloneNode(false) as HTMLElement;
            ghostHeaderRow.style.gridTemplateColumns = '1fr';
            ghostHeaderRow.style.height = `${headerRow.getBoundingClientRect().height}px`;
            ghostHeaderRow.appendChild(sourceCell.cloneNode(true));
            ghostHead.appendChild(ghostHeaderRow);
            ghostTable.appendChild(ghostHead);

            // Clone the body cells of the dragged column into the ghost
            const ghostBody = document.createElement('tbody');
            const bodyRows = Array.from(table.querySelectorAll('tbody tr:not(.group-header)')).slice(0, 40) as HTMLElement[];
            bodyRows.forEach(bodyRow => {
                const bodyCell = bodyRow.children[leadingCellCount + sourceIndex];
                if (!bodyCell) {
                    return;
                }

                // Keep the measured row height so the ghost rows match the real rows
                const ghostRow = bodyRow.cloneNode(false) as HTMLElement;
                ghostRow.style.gridTemplateColumns = '1fr';
                ghostRow.style.height = `${bodyRow.getBoundingClientRect().height}px`;
                ghostRow.appendChild(bodyCell.cloneNode(true));
                ghostBody.appendChild(ghostRow);
            });
            ghostTable.appendChild(ghostBody);
            return ghostTable.outerHTML;
        };

        // Resolve the column index under the cursor
        const resolveTargetIndex = (clientX: number) => {
            const index = cellRects.findIndex(rect => clientX >= rect.left && clientX <= rect.right);
            return index === -1 ? null : index;
        };

        // Move the ghost of the dragged column along with the cursor
        const handleMouseMove = (moveEvent: MouseEvent) => {
            // Only start the visual drag after a small movement threshold
            if (!dragging && Math.abs(moveEvent.clientX - startX) < 5) {
                return;
            }

            // Clone the column contents into the ghost once the drag really starts
            if (!dragging) {
                setDragGhostHtml(buildGhostHtml());
            }
            dragging = true;
            suppressHeaderClickRef.current = true;

            // Resolve the column the ghost currently hovers as the drop target
            targetIndex = resolveTargetIndex(moveEvent.clientX);

            // Include the scroll offset of the wrapper since the ghost is positioned in content coordinates
            const scrollLeft = tableWrapperRef.current?.scrollLeft ?? 0;
            const scrollTop = tableWrapperRef.current?.scrollTop ?? 0;

            // Position the ghost under the cursor, clamped to the table bounds
            const minLeft = tableRect.left - wrapperRect.left + scrollLeft;
            const maxLeft = tableRect.right - wrapperRect.left + scrollLeft - sourceRect.width;
            const ghostLeft = Math.min(Math.max(moveEvent.clientX - grabOffset - wrapperRect.left + scrollLeft, minLeft), Math.max(minLeft, maxLeft));
            setDragHighlight({
                left: ghostLeft,
                top: tableRect.top - wrapperRect.top + scrollTop,
                width: sourceRect.width,
                height: tableRect.height,
            });
        };

        // Persist the new column order once the mouse button is released
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            setDragHighlight(null);
            setDragGhostHtml(null);
            if (dragging && targetIndex !== null) {
                handleColumnReorderEnd(field, targetIndex);
            }

            // Allow the sorting click again after this event finishes
            setTimeout(() => { suppressHeaderClickRef.current = false; }, 0);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Handle a sorting click on a column header name
    const handleHeaderSortClick = (column: { disableOrdering?: boolean, orderField?: string, field: string }) => {
        if (suppressHeaderClickRef.current || column.disableOrdering) {
            return;
        }
        handleColumnOrdering(column.orderField ? column.orderField : column.field);
    };

    // Show the full column name in a tooltip when the label is truncated
    const handleHeaderLabelMouseEnter = (event: React.MouseEvent, label: string) => {
        const element = event.currentTarget as HTMLElement;
        if (element.scrollWidth > element.clientWidth) {
            const rect = element.getBoundingClientRect();
            setListTooltip({
                x: rect.left + rect.width / 2,
                y: rect.bottom + 8,
                content: label,
                position: 'below',
            });
        }
    };

    // Hide the column name tooltip when the cursor leaves the label
    const handleHeaderLabelMouseLeave = () => {
        setListTooltip(null);
    };

    // Don't show the delete button in lists with in-active or deleted query params, otherwise the item may be double 'deleted'
    const showDeleteButton = () => {
        if (showDeleteOrDeactivate === 'fully_delete') {
            return true;
        } else if (showDeleteOrDeactivate === 'flag_deactivated' || showDeleteOrDeactivate === 'flag_deleted') {

            const isActive = queryParams['is_active'] === 'false' || queryParams['is_active'] === false;
            const isDeleted = queryParams['deleted'] === 'true' || queryParams['deleted'] === true;

            if ((!isActive && !isDeleted) || !filters) {
                return true
            }
        }
        return false;
    };

    // Determine to show the revert button
    const showRevertButton = () => {
        if (!showDeleteOrDeactivate) {
            return false;
        }

        const isDeleted = queryParams['deleted'] === 'true' || queryParams['deleted'] === true;
        const isInactive = queryParams['is_active'] === 'false' || queryParams['is_active'] === false;

        return isDeleted || isInactive;
    };

    // Determine the grid template columns based on the columns.width from the list config
    const getGridTemplateColumns = () => {
        // Start with the rendered display columns, live resize widths win over the config
        let gridColumns = displayColumns.map(column => ({
            ...column,
            width: resizeWidths[column.field] ?? column.width,
        }));

        // Add a fixed width column if delete or deactivate button should be shown
        if (showDeleteOrDeactivate) {
            gridColumns.push({ width: '20px', field: 'delete', label: '' });
        }

        // Add a fixed width column at the beginning if the list is draggable
        if (isDraggable) {
            gridColumns.unshift({ width: '22px', field: 'drag', label: '' });
        }

        // Add a fixed width selection checkbox column as the true leftmost column
        if (bulkActions) {
            gridColumns.unshift({ width: '30px', field: 'select', label: '' });
        }

        // Add a flexible filler column absorbing the leftover space at the end
        if (showFillerColumn) {
            gridColumns.push({ width: 'minmax(0, 1fr)', field: 'filler', label: '' });
        }

        // Return the grid columns as string. With the feature the flexible
        // tracks become content independent, otherwise a header and a row
        // resolve a cramped fr track to their own min content and misalign
        return gridColumns.map(col => (
            hasCustomListColumns && col.width.endsWith('fr') && !col.width.startsWith('minmax') ? `minmax(0, ${col.width})` : col.width
        )).join(' ');
    };

    // Group the rows by field if given
    const rowsData = groupByField ? groupDataByField(t, data, groupByField.field) : { ungrouped: data };

    // Render the rows, grouped or not grouped
    const renderRows = (groupedData: Record<string, T[]>) => {
        return Object.entries(groupedData).map(([groupName, items]) => (
            <React.Fragment key={groupName}>
                {groupByField && ( 
                    // Show a group header if the rows are grouped by a field
                    <tr className='group-header'>
                        {groupByField.formatValue
                            ? groupByField.formatValue(groupName)
                            : groupByField.translateValue
                                ? t(`${query.object}.${groupByField.field}.${groupName}`)
                                : groupName
                        }
                    </tr>
                )}
                {items.map((row, index) => (
                    isDraggable ? (
                        <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                            {(provided, snapshot) => (
                                <ListRow<T>
                                    key={row.id}
                                    row={row}
                                    gridClass={getGridTemplateColumns()}
                                    query={query}
                                    objectName={objectName}
                                    columns={displayColumns}
                                    showFillerColumn={showFillerColumn}
                                    isDraggable={true}
                                    showDeleteOrDeactivate={showDeleteOrDeactivate}
                                    showDeleteButton={showDeleteButton()}
                                    showRevertButton={showRevertButton()}
                                    allowEditingInactiveRows={allowEditingInactiveRows}
                                    detailPageUrl={detailPageUrl}
                                    onRowClick={onRowClick}
                                    detailModal={detailModal}
                                    detailModalName={detailModalName}
                                    formModal={formModal}
                                    editModal={editModal}
                                    releaseViewModal={releaseViewModal}
                                    wizardModal={wizardModal}
                                    modalSize={modalSize}
                                    detailModalSize={detailModalSize}
                                    provided={provided}
                                    snapshot={snapshot}
                                    refetchList={refetchList}
                                    bulkActions={bulkActions}
                                    isRowSelected={bulkActions ? checkRowSelected(selection, row.id) : undefined}
                                    onToggleRowSelect={toggleRowSelection}
                                />
                            )}
                        </Draggable>
                    ) : (
                        <ListRow<T>
                            key={row.id}
                            row={row}
                            gridClass={getGridTemplateColumns()}
                            query={query}
                            objectName={objectName}
                            columns={displayColumns}
                            showFillerColumn={showFillerColumn}
                            showDeleteOrDeactivate={showDeleteOrDeactivate}
                            showDeleteButton={showDeleteButton()}
                            showRevertButton={showRevertButton()}
                            allowEditingInactiveRows={allowEditingInactiveRows}
                            detailPageUrl={detailPageUrl}
                            onRowClick={onRowClick}
                            detailModal={detailModal}
                            detailModalName={detailModalName}
                            formModal={formModal}
                            editModal={editModal}
                            releaseViewModal={releaseViewModal}
                            wizardModal={wizardModal}
                            modalSize={modalSize}
                            detailModalSize={detailModalSize}
                            refetchList={refetchList}
                            bulkActions={bulkActions}
                            isRowSelected={bulkActions ? checkRowSelected(selection, row.id) : undefined}
                            onToggleRowSelect={toggleRowSelection}
                        />
                    )
                ))}
            </React.Fragment>
        ));
    };

    // Calculate the list tooltip position
    useLayoutEffect(() => {
        if (listTooltip && tooltipRef.current) {
            // Get the size of the tooltip from the tooltip ref
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const tooltipHeight = tooltipRef.current.offsetHeight;

            // Calculate the tooltip position centered below the item
            if (listTooltip.position === 'below') {
                setTooltipPosition({
                    top: listTooltip.y,
                    left: listTooltip.x - tooltipWidth / 2,
                });

            // Calculate the tooltip position left of the item
            } else {
                setTooltipPosition({
                    top: listTooltip.y - (tooltipHeight) / 2,
                    left: listTooltip.x - tooltipWidth,
                });
            }

            // Make the tooltip visible afterwards
            setTooltipVisible(true);
        } else {
            setTooltipVisible(false);
        }
    }, [listTooltip]);
    
    return (
        <div className='list-component'>
            <ListButtons
                query={query}
                objectName={objectName}
                filters={filters}
                activeQuickFilter={activeQuickFilter}
                onActiveQuickFilterChange={onActiveQuickFilterChange}
                filterFields={filterFields}
                quickFilterSize={quickFilterSize}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filterDropdownData={filterDropdownData}
                bulkActions={bulkActions}
                moreMenu={moreMenu}
                inactiveItemsActive={inactiveItemsActive}
                onToggleInactiveItems={onToggleInactiveItems}
                onExportAll={onExportAll}
                showSearch={showSearch}
                showAddButton={showAddButton}
                showImport={showImport}
                showExport={showExport}
                showPerWeek={showPerWeek}
                weekNavigation={weekNavigation}
                headerTitle={headerTitle}
                backButton={backButton}
                actionButton={actionButton}
                secondaryButton={secondaryButton}
                showColumnSelector={showColumnSelector}
                postNewItem={postNewItem}
                openPageUrl={openPageUrl}
                formModal={formModal}
                getFormModal={getFormModal}
                getFormModalTitle={getFormModalTitle}
                detailModal={detailModal}
                modalSize={modalSize}
                getModalSize={getModalSize}
                buttonSize={buttonSize}
                changeViewButton={changeViewButton}
                renderHeaderInfo={renderHeaderInfo}
                renderTrailingInfo={renderTrailingInfo}
                refetchList={refetchList}
                tableWidthClass={tableWidthClass}
            />
            {renderNotice && renderNotice() && (
                // Render the notice line of the list between the header buttons and the table
                <div className={`${tableWidthClass} list-notice`}>
                    {renderNotice()}
                </div>
            )}
            <div className={`${tableWidthClass} ${tableWidthClass === 'container-wide-filled' ? 'list-full-width' : ''}`}>
                <div className={`${isPaginated ? 'paginated-list' : 'non-paginated-list'} list-table-wrapper`} ref={tableWrapperRef}>
                    <table className={`list ${tableClassName ?? ''}`}
                           style={hasCustomListColumns ? { minWidth: tableMinWidth } : undefined}>
                        <thead>
                            <tr style={{ gridTemplateColumns: getGridTemplateColumns() }}>
                                {bulkActions && (
                                    // Show the select all checkbox, reflects the current selection versus the total rows
                                    <th className={`${loading !== 'loading' ? 'border-bottom' : ''} disable-ordering checkbox-column`}>
                                        <ListSelectionCheckbox
                                            id='list-select-all'
                                            checked={listMetaData.totalRows > 0 && selectedCount === listMetaData.totalRows}
                                            indeterminate={selectedCount > 0 && selectedCount < listMetaData.totalRows}
                                            onChange={toggleSelectAll}
                                            ariaLabel={t('bulk_actions.general.select_all_label')}
                                        />
                                    </th>
                                )}
                                {isDraggable && (
                                    // Add an extra element to align the headers with the drag handle column
                                    <th className={`${loading !== 'loading' ? 'border-bottom' : ''} disable-ordering`}></th>
                                )}
                                {displayColumns.map((column) => {
                                    // Get the backend ordering field
                                    const orderingField = column.orderField ? column.orderField : column.field;

                                    // Determine if the column is sorted and is descending
                                    const isSorted = ordering && (ordering === orderingField || ordering === `-${orderingField}`);
                                    const isDescending = ordering && ordering.startsWith(`-${orderingField}`);

                                    // Determine if the sorting of this column was just cleared back to the default sorting
                                    const isCleared = !ordering && clearedOrderingColumn === orderingField;
                                    
                                    return (
                                        // Render the header columns, with the feature the header drags to
                                        // reorder and only a click on the name itself sorts the list
                                        <th key={column.field}
                                            onClick={hasCustomListColumns ? undefined : () => handleHeaderSortClick(column)}
                                            onMouseDown={hasCustomListColumns ? (event) => startColumnDrag(event, column.field) : undefined}
                                            className={`${loading !== 'loading' ? 'border-bottom' : ''} ${column.disableOrdering ? 'disable-ordering' : ''} ${hasCustomListColumns ? 'resizable-column' : ''}`}>
                                            <span className={`header-label ${hasCustomListColumns && !column.disableOrdering ? 'column-header-label' : ''}`}
                                                  onClick={hasCustomListColumns ? () => handleHeaderSortClick(column) : undefined}
                                                  onMouseEnter={(event) => handleHeaderLabelMouseEnter(event, column.rawLabel ? column.label : t(column.label))}
                                                  onMouseLeave={handleHeaderLabelMouseLeave}>
                                                {column.rawLabel ? column.label : t(column.label)}
                                            </span>
                                            {isSorted && (
                                                <FontAwesomeIcon
                                                    icon={isDescending ? faCaretDown : faCaretUp}
                                                    className={'sorting-icon'} />
                                            )}
                                            {isCleared && (
                                                // Show a dash as the in between state, the list uses its default sorting
                                                <FontAwesomeIcon
                                                    icon={faMinus}
                                                    className={'sorting-icon sorting-icon-cleared'} />
                                            )}
                                            {hasCustomListColumns && (
                                                // Drag handle on the column edge to resize the column width
                                                <span className='column-resize-handle'
                                                      onMouseDown={(event) => startColumnResize(event, column.field)}
                                                      onClick={(event) => event.stopPropagation()} />
                                            )}
                                        </th>
                                    );
                                })}
                                {showDeleteButton() && (
                                    // Add an extra element in case there is a delete button in this list
                                    <th className={`${loading !== 'loading' ? 'border-bottom' : ''}`}></th>
                                )}
                                {showFillerColumn && (
                                    // Add an empty header for the trailing filler column
                                    <th className={`${loading !== 'loading' ? 'border-bottom' : ''} disable-ordering`}></th>
                                )}
                            </tr>
                        </thead>
                        {isPaginated && (loading === 'loading' || loading === 'show-loader') && data.length === 0 ? (
                            // For paginated lists, if the list has no data and is loading, show loading rows. Don't show the loading rows on refetches when the list already has data.
                            <tbody>
                                {Array.from({ length: pageSize }, (_, rowIndex) => (
                                    <tr key={rowIndex} style={{ gridTemplateColumns: getGridTemplateColumns() }}>
                                        {bulkActions && (
                                            // Keep the columns aligned with the checkbox column while loading
                                            <td key='select'></td>
                                        )}
                                        {displayColumns.map((column) => (
                                            <td key={column.field}>
                                                <div className="loading-rectangle" />
                                            </td>
                                        ))}
                                        {showFillerColumn && (
                                            // Keep the columns aligned with the filler column while loading
                                            <td key='filler'></td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        ) : (
                            isDraggable ? (
                                <DragDropContext onDragEnd={(result) => {
                                    handleDragRow(
                                        result, 
                                        data, 
                                        handleDragOrdering, 
                                        query.object, 
                                        saveData,
                                        saveBatchData,
                                        setFloatingAlert,
                                        draggingApiUrl,
                                    )}}>
                                    <StrictModeDroppable droppableId="list">
                                        {(provided) => (
                                            <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                                {renderRows(rowsData)}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </StrictModeDroppable>
                                </DragDropContext>
                            ) : (
                                <tbody>
                                    {renderRows(rowsData)}
                                </tbody>
                            )
                        )}
                    </table>
                    {dragHighlight && (
                        // Shows a ghost of the whole dragged column moving with the cursor
                        <div className={`column-drag-highlight ${dragGhostHtml ? 'column-drag-ghost' : ''}`}
                             style={{
                                left: dragHighlight.left,
                                top: dragHighlight.top,
                                width: dragHighlight.width,
                                height: dragHighlight.height,
                             }}
                             dangerouslySetInnerHTML={dragGhostHtml ? { __html: dragGhostHtml } : undefined} />
                    )}
                    {loading == 'success' && data.length === 0 && (
                        // Shows an empty list box
                        <div className='empty-list'>
                            <p>
                                {t("general.empty_list_text", { object_name_singular: t(`${objectName}.singular`), object_name_plural: t(`${objectName}.plural`)})}
                            </p>
                        </div>
                    )}
                </div>            
                {isPaginated && listMetaData.totalRows > pageSize ? (
                    <div className='pagination-buttons'>
                        <button onClick={() => handlePageChange(listMetaData.currentPage - 1)}
                                disabled={listMetaData.currentPage === 1}
                                className='button button-small button-secondary'>
                            {t('general.previous')}
                        </button>
                        <span className="pagination-pages">
                            {t('general.page_total_string', { page: listMetaData.currentPage, totalPages: Math.ceil(listMetaData.totalRows / pageSize).toLocaleString() })}
                        </span>
                        <button onClick={() => handlePageChange(listMetaData.currentPage + 1)}
                                disabled={listMetaData.currentPage === Math.ceil(listMetaData.totalRows / pageSize)}
                                className='button button-small button-secondary'>
                            {t('general.next')}
                        </button>
                    </div>
                ) : null}
            </div>
            {listTooltip && (
                <div className="list-tooltip"
                     ref={tooltipRef}
                     style={{
                        top: tooltipPosition?.top ?? -9999,
                        left: tooltipPosition?.left ?? -9999,
                        opacity: tooltipVisible ? 1 : 0,
                    }}>
                    {listTooltip.content}
                </div>
            )}
            {bulkActions && selectedCount > 0 && (
                // Show the bulk actions panel whenever at least one row is selected
                <BulkActionsPanel bulkActions={bulkActions} initialKind={panelInitialKind ?? undefined} restoreMode={inactiveItemsView} />
            )}
        </div>
    );
};

export default ListComponent;